import { nextTick, unref } from 'vue';
import { until, useScroll } from '@vueuse/core';

export const ERROR_CLASS = 'has-status-error';

/**
 * Scrolls into view the first element with an error within a given context.
 * The context can be a DOM element or a Vue reference to a component or a DOM element.
 * It waits for two ticks to allow the Vue component to re-render, if needed, before
 * scrolling. It then scrolls to the first element containing the `ERROR_CLASS`
 * and focuses on it after the scrolling stops.
 *
 * @param {HTMLElement|Vue.Ref<HTMLElement|Vue.Component>} context - The context within which to search for the error
 *              element. This could be a reference to a Vue component, an HTML element, or a ref object pointing to one
 *              of these types. If this parameter is not provided, the document body is used as context.
 */
export async function scrollToFirstError(context) {
    const contextHTMLElement = context ? (unref(context).$el || unref(context)) : document.body;
    const { isScrolling } = useScroll(window.document);

    // Wait 2 ticks for re-rendering
    await nextTick();
    await nextTick();

    const firstErrorElement = contextHTMLElement.querySelector(`.${ERROR_CLASS}`);
    firstErrorElement?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
    });

    // Wait for the scroll to start and finish before focusing the element, so scrolling isn't skipped
    await until(isScrolling).toBe(true);
    await until(isScrolling).toBe(false);

    firstErrorElement?.focus();
}
